import React, { useState, useEffect, useCallback } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextValidator } from "react-material-ui-form-validator";
import CircularProgress from "@material-ui/core/CircularProgress";
import { addressSelectionService, addressSuggestionService } from "../../../../services/services";
import { debounce } from 'lodash'
import styles from "../../AccountVerificationForm/AccountVerificationForm.module.css"

export default function AddressAutocomplete(props) {
    const {
        baseUrl,
        behaviors,
        question,
        handlerAddressChange,
        value,
        stateOptions,
        disabled
    } = props;

    const [options, setOptions] = useState([]);
    const [addressValue, setAddressValue] = useState({text: value || ""});
    const [inputValue, setInputValue] = useState(value || "");
    const [isValidAddress, setIsValidAddress] = useState(true);
    const [open, setOpen] = useState(false);
    const autoloading = open;
    const [completeAddress, setCompleteAddress] = useState({
        address: { value: "", textValue: "" },
        unit: { value: "", textValue: "" },
        city: { value: "", textValue: "" },
        state: { value: "", textValue: "" },
        zip: { value: "", textValue: "" },
    });

    useEffect(() => {
        setAddressValue({ text: value || "" });
        setInputValue(value);
    }, [value]);

    let suggestions = async (text) => {
        return await addressSuggestionService(baseUrl, text)
            .then((response) =>
                response.suggestions?.map((suggestion) => {
                    let secondary = "";
                    if (suggestion.secondary) {
                        if (suggestion.entries > 1) {
                            suggestion.secondary +=
                                " (" + suggestion.entries + " entries)";
                        }
                        secondary = suggestion.secondary;                    }
                    return {
                        ...suggestion,
                        text: `${suggestion.street_line} ${secondary} ${suggestion.city}, ${suggestion.state}, ${suggestion.zipcode}`,
                    };
                })
            )
            .catch((error) => console.error(error));
    };

    const onFocus = (event) => {
        if (event.target.autocomplete) {
            event.target.autocomplete = "whatever";
        }
    };

    const getSuggestions = async (newInputValue) => {

        if(behaviors?.is_manual_address_enabled && behaviors?.disable_smartystreets_at_all)
            return

        if (newInputValue) {
            try {
                let options = await suggestions(newInputValue);
                if (options) {
                    setOptions(options);
                } else {
                    setOptions([]);
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    const debouncedSave = useCallback(
        debounce((newValue) => getSuggestions(newValue), 500),
        []
    );

    // Checking address
    const addressServiceCall = async (address) =>
        addressSelectionService(baseUrl, address)
            .then((response) => {
                const result = response;
                if (result.length > 0) {
                    for (var i = 0; i < result.length; i++) {
                        let data = result[i];
                        if (data) {
                            const barcode = data.deliveryPointBarcode;
                            address.zip = {
                                value: data.components.zipcode,
                                textValue: data.components.zipcode,
                            };
                            handlerAddressChange(true, address, barcode);
                            setIsValidAddress(true);
                        } else {
                            handlerAddressChange(false, address);
                        }
                    }
                }
            })
            .catch(() => {
                handlerAddressChange(false, address);
            });

    const autocompleteAddress = (newInputValue) => {
        setInputValue(newInputValue);
        debouncedSave(newInputValue);
        const completeAddress = {
            address: { value: newInputValue, textValue: newInputValue },
        };
        handlerAddressChange(true, completeAddress);
    }

    const addressInputAutocomplete = async (newInputValue, reason) => {
        if (reason === "reset") {
            if (newInputValue.includes("entries")) {
                autocompleteAddress(newInputValue);
                setOpen(true);
            } else {
                setOpen(false);
            }
        } else if (reason === "input") {
            if (newInputValue.length > 0) autocompleteAddress(newInputValue);
        }
    };

    const clearAddress = () => {
        setAddressValue({ text: "" });
        const resetAddress = {
            ...completeAddress,
            address: { value: "", textValue: "" },
            unit: { value: "", textValue: "" },
            city: { value: "", textValue: "" },
            state: { value: "", textValue: "" },
            zip: { value: "", textValue: "" },
        };
        handlerAddressChange(true, resetAddress);
        setIsValidAddress(true);
        setOpen(false);
    };

    const addressAutocomplete = (newValue, reason) => {
        const { entries } = newValue || 0;
        
        if (reason === "clear" && !newValue) clearAddress();

        if (reason === "select-option") {
            if (entries <= 1) {
                setOpen(false);
                setOptions(newValue ? [newValue, ...options] : options);
                setAddressValue(newValue);
                if (newValue) {
                    const address = newValue.street_line;
                    setAddressValue({ text: address });
                    let city = newValue.city;
                    let state = newValue.state;
                    let unit = newValue.secondary;
                    let stateValue = stateOptions.find(
                        (element) => element.value === state
                    );

                    const newAddress = {
                        address: { value: address, textValue: address },
                        city: { value: city, textValue: city },
                        unit: { value: unit, textValue: unit },
                    };
                    if (stateValue) {
                        state = {
                            value: stateValue.value,
                            textValue: stateValue.label,
                        };
                        setCompleteAddress({ ...newAddress, state });
                        addressServiceCall({ ...newAddress, state });
                    } else {
                        addressServiceCall(newAddress);
                        setCompleteAddress((prev) => ({ ...prev, newAddress }));
                    }
                } else {
                    clearAddress();
                }
            } else {
                setOpen(true);
            }
        }
    };

    return (
        <Autocomplete
            id={question.id}
            name={question.name}
            open={open}
            freeSolo={true}
            disabled={disabled || question.disabled}
            disableCloseOnSelect={open}
            onOpen={(component) => {
                
                if(behaviors?.is_manual_address_enabled && behaviors?.disable_smartystreets_at_all)
                    return

                if (component.target.value === addressValue.text) {
                    setOpen(false);
                } else {
                    setOpen(true);
                }
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={(option, value) => 
                option.text === value.text
            }
            getOptionLabel={(option) => option.text}
            filterOptions={(x) => x}
            loading={autoloading}
            options={options}
            noOptionsText="No address match with the search"
            value={addressValue}
            onChange={(_, newValue, reason) => 
                addressAutocomplete(newValue, reason)
            }
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => 
                addressInputAutocomplete(newInputValue, reason)
            }
            renderInput={(params) => (
                <TextValidator
                    {...params}
                    id={question.id}
                    label={
                        question.required ? (
                            <div>
                                {question.label}
                                <span
                                    style={{
                                        color: "red",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    *
                                </span>
                                {" "}
                                {question.helpText &&
                                <div className={ styles.tooltip}> ?
                                    <span className={ styles.tooltipText} >{question.helpText}</span>
                                </div>
                                }
                            </div>
                        ) : (
                            <div>
                                {question.label}
                                <span style={{ fontSize: "16px"}} > {" "} </span>
                                {question.helpText &&
                                  <div className={ styles.tooltip}> ?
                                    <span className={ styles.tooltipText} >{question.helpText}</span>
                                  </div>
                                }
                            </div>                            
                        )
                    }
                    autoComplete="none"
                    InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        autoComplete: "new-password",
                        endAdornment: (
                            <React.Fragment>
                                {autoloading ? (
                                    <CircularProgress
                                        color="inherit"
                                        size={25}
                                    />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                    validators={question.field.validators}
                    errorMessages={question.field.errorMessages}
                    error={!isValidAddress}
                    helperText={
                        !isValidAddress
                            ? "We're sorry, but we couldn't validate this address."
                            : null
                    }
                    name={question.name}
                    value={value}
                    placeholder={question.placeholder}
                    fullWidth
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onFocus={onFocus}
                />
            )}
        />
    );
}
